// Thin Firebase Realtime DB wrapper used as the WebRTC signaling channel
// between the desktop host and the mobile camera client.
//
// Schema (per session):
//
//   webrtcSignaling/{sessionId}/
//     createdAt: server timestamp
//     offer:        { sdp, type } | null
//     answer:       { sdp, type } | null
//     desktopIce/{pushKey}: RTCIceCandidateInit  // desktop -> mobile
//     mobileIce/{pushKey}:  RTCIceCandidateInit  // mobile  -> desktop
//     control:      { command: 'switchCamera' | 'mute' | 'unmute' | ... }
//     status:       'waiting' | 'connecting' | 'connected' | 'closed'
//
// All paths are torn down via remove() when either side disconnects.
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getDatabase, ref, set, onValue, off, push, onChildAdded, remove, serverTimestamp, update,
} from 'firebase/database';
import { firebaseCredentials } from 'Api';

// Reuse the existing Firebase app if one was already initialized elsewhere
// (uploadUtils, app.jsx etc. each call initializeApp on module load).
const getFirebaseDatabase = () => {
  const app = getApps().length ? getApp() : initializeApp(firebaseCredentials);
  return getDatabase(app);
};

const sessionPath = (sessionId) => `webrtcSignaling/${sessionId}`;

export const createSignaling = (sessionId) => {
  const db = getFirebaseDatabase();
  const root = ref(db, sessionPath(sessionId));
  const offerRef = ref(db, `${sessionPath(sessionId)}/offer`);
  const answerRef = ref(db, `${sessionPath(sessionId)}/answer`);
  const desktopIceRef = ref(db, `${sessionPath(sessionId)}/desktopIce`);
  const mobileIceRef = ref(db, `${sessionPath(sessionId)}/mobileIce`);
  const controlRef = ref(db, `${sessionPath(sessionId)}/control`);
  const mobileControlRef = ref(db, `${sessionPath(sessionId)}/mobileControl`);
  const statusRef = ref(db, `${sessionPath(sessionId)}/status`);

  // Track every listener we attach so cleanup is reliable.
  const detachers = [];

  const init = () => set(root, {
    createdAt: serverTimestamp(),
    status: 'waiting',
    offer: null,
    answer: null,
  });

  const writeOffer = (sdp) => set(offerRef, sdp);
  const writeAnswer = (sdp) => set(answerRef, sdp);
  const onOffer = (cb) => {
    const handler = onValue(offerRef, (snap) => {
      const v = snap.val();
      if (v) cb(v);
    });
    detachers.push(() => off(offerRef, 'value', handler));
  };
  const onAnswer = (cb) => {
    const handler = onValue(answerRef, (snap) => {
      const v = snap.val();
      if (v) cb(v);
    });
    detachers.push(() => off(answerRef, 'value', handler));
  };

  const sendDesktopIce = (candidate) => push(desktopIceRef, candidate);
  const sendMobileIce = (candidate) => push(mobileIceRef, candidate);
  const onDesktopIce = (cb) => {
    const handler = onChildAdded(desktopIceRef, (snap) => {
      const v = snap.val();
      if (v) cb(v);
    });
    detachers.push(() => off(desktopIceRef, 'child_added', handler));
  };
  const onMobileIce = (cb) => {
    const handler = onChildAdded(mobileIceRef, (snap) => {
      const v = snap.val();
      if (v) cb(v);
    });
    detachers.push(() => off(mobileIceRef, 'child_added', handler));
  };

  // Build the value to write — accepts either a plain string command or an
  // object payload. Always stamps a unique `ts` so RTDB onValue fires even
  // when the rest of the payload is identical to the previous write.
  const buildPayload = (input) => {
    const base = (typeof input === 'string') ? { command: input } : (input || {});
    return { ...base, ts: Date.now() };
  };

  // Desktop → mobile (mute / unmute / state mirror)
  const sendControl = (payload) => set(controlRef, buildPayload(payload));
  const onControl = (cb) => {
    const handler = onValue(controlRef, (snap) => {
      const v = snap.val();
      if (v && v.command) cb(v);
    });
    detachers.push(() => off(controlRef, 'value', handler));
  };

  // Mobile → desktop (start / skip / finish / re-record)
  const sendMobileControl = (payload) => set(mobileControlRef, buildPayload(payload));
  const onMobileControl = (cb) => {
    const handler = onValue(mobileControlRef, (snap) => {
      const v = snap.val();
      if (v && v.command) cb(v);
    });
    detachers.push(() => off(mobileControlRef, 'value', handler));
  };

  const setStatus = (status) => update(root, { status });
  const onStatus = (cb) => {
    const handler = onValue(statusRef, (snap) => {
      const v = snap.val();
      if (v) cb(v);
    });
    detachers.push(() => off(statusRef, 'value', handler));
  };

  const teardown = async () => {
    detachers.forEach((d) => { try { d(); } catch (e) { /* noop */ } });
    detachers.length = 0;
    try { await remove(root); } catch (e) { /* noop */ }
  };

  return {
    init,
    writeOffer,
    writeAnswer,
    onOffer,
    onAnswer,
    sendDesktopIce,
    sendMobileIce,
    onDesktopIce,
    onMobileIce,
    sendControl,
    onControl,
    sendMobileControl,
    onMobileControl,
    setStatus,
    onStatus,
    teardown,
  };
};

// Random short session ID — base36, 10 chars (~50 bits, plenty for ephemeral pairings).
export const newSessionId = () => `${Math.random().toString(36).slice(2, 7)}${Math.random().toString(36).slice(2, 7)}`;
